import { AuthUser } from '../core/state/auth-store';
import { createStore } from '../core/state/store';
import { GameTab } from './tabs';
import { icon } from './icons';
import { t } from './i18n/i18n';
import { audioStore, setSfxVolume, setVolume, toggleMuted } from './audio';
import { mountToastContainer, unmountToastContainer } from './toast';
import { tabBadgeStore } from './tab-badges';
import { mountClickSound } from './click-sound';
import { fetchStoreBalance } from '../screens/shop/shop-api';
import { mountHoloTilt } from './holo-tilt';
import { mountRandomGlitch } from './random-glitch';
import { mountShopTip } from './shop-tip';
import { deltaVWordHtml } from './delta-v-word';
import { fetchProfile } from '../screens/profile/profile-api';
import { titleBadgeAttrs } from './title-badge';

/**
 * Mobile-first app shell: a top bar (title + sign out), a scrollable content
 * area, and a fixed bottom tab bar (>=44px targets, safe-area-aware for
 * notched phones). Tabs are swapped by tearing down the previous tab's mount
 * and mounting the next one into the same content div — same teardown
 * contract every screen in this app already uses.
 *
 * Responsive by construction, not by a separate "mobile" code path: the tab
 * bar and content area use relative sizing/flex throughout, so the same
 * markup works from a small phone up through desktop. There is no
 * desktop-only layout to maintain in parallel.
 */
const activeTabStore = createStore<string>('');

/** How often the header re-reads the Delta-V balance — spending happens on screens that cannot push to it. */
const DELTA_V_REFRESH_MS = 20_000;

export function mountShell(root: HTMLElement, user: AuthUser, tabs: GameTab[], onLogout: () => void): () => void {
  if (tabs.length === 0) throw new Error('mountShell: at least one tab is required');

  root.innerHTML = `
    <div class="relative isolate h-screen flex flex-col bg-bg">
      <div class="holo-backdrop" aria-hidden="true"></div>
      <div class="holo-scan" aria-hidden="true"></div>
      <header class="holo-bar shrink-0 flex items-center justify-between gap-3 px-4 py-3 border-b border-border pt-[calc(env(safe-area-inset-top)+12px)]">
        <div class="shell-id relative min-w-0 hidden sm:block">
          <div class="holo-title font-heading text-gold text-lg leading-tight truncate">Dune in Conflict</div>
          <div class="text-text-dim text-xs truncate">${user.username ?? user.email}</div>
          <!-- Desktop: the equipped title, pinned over the bottom edge of this block exactly like the phone profile button's. -->
          <span data-shell-title hidden></span>
        </div>
        <!-- Phones: the title block gives way to the profile button (and the bottom bar drops its Profile tab). -->
        ${tabs.some((tab) => tab.id === 'profile') ? `
        <button type="button" id="shell-profile" aria-label="${t('nav.profile')}"
          class="sm:hidden holo-chip relative min-h-11 min-w-0 pl-2 pr-3 flex items-center gap-2 rounded border border-border text-text-dim">
          <span class="text-xl leading-none shrink-0" aria-hidden="true">${icon('profile')}</span>
          <span class="text-xs truncate max-w-[9rem]">${user.username ?? user.email}</span>
          <!-- The equipped title, pinned over the bottom edge of this button like a badge (overflows it downward and a
               little to the sides); same material as the profile card's plate. Filled in by refreshTitleBadge(). -->
          <span data-shell-title hidden></span>
        </button>` : ''}
        <div class="shrink-0 flex items-center gap-2">
        <!-- Delta-V balance. Hidden until the first fetch lands rather than
             shown as a placeholder zero, which would read as "you are broke"
             on every page load. -->
        <button type="button" id="shell-deltav" hidden data-tab-link="shop"
          class="deltav-gold holo-chip holo-chip--live shrink-0 min-h-11 px-3 flex items-center gap-1.5 rounded border"
          title="${t('shell.deltaV')}" aria-label="${t('shell.deltaV')}">
          <span id="shell-deltav-value" class="text-sm tabular-nums font-heading font-bold">0</span>
          <!-- The word, not the rocket; phones keep the glyph (header space is tight there). -->
          <span class="hidden sm:inline" aria-hidden="true">${deltaVWordHtml('sm')}</span>
          <span class="sm:hidden text-base leading-none" aria-hidden="true">${icon('deltaV')}</span>
          <span class="sr-only">${t('shell.deltaV')}</span>
        </button>
        <div class="relative" id="shell-audio-wrap">
          <button id="shell-mute" type="button" aria-expanded="false" aria-controls="shell-audio-popup"
            class="holo-chip shrink-0 min-w-11 min-h-11 px-3 flex items-center justify-center rounded border border-border text-text-dim hover:border-gold hover:text-gold">
          </button>
          <div id="shell-audio-popup" class="hidden absolute right-0 top-full mt-2 z-50 w-52 glass-panel p-3 flex flex-col gap-3">
            <button id="shell-mute-toggle" type="button"
              class="font-button min-h-11 w-full rounded bg-surface-2 border border-border text-text text-xs hover:border-gold hover:text-gold transition-colors duration-[120ms]">
              ${t('shell.mute')}
            </button>
            <div class="flex flex-col gap-1">
              <label for="shell-volume-slider" class="font-heading text-[10px] text-text-dim uppercase tracking-wide">${t('shell.music')}</label>
              <input id="shell-volume-slider" type="range" min="0" max="100" step="1"
                class="w-full h-1.5 accent-gold cursor-pointer" />
            </div>
            <div class="flex flex-col gap-1">
              <label for="shell-sfx-slider" class="font-heading text-[10px] text-text-dim uppercase tracking-wide">${t('shell.effects')}</label>
              <input id="shell-sfx-slider" type="range" min="0" max="100" step="1"
                class="w-full h-1.5 accent-gold cursor-pointer" />
            </div>
          </div>
        </div>
        <button id="shell-logout" type="button" aria-label="${t('common.signOut')}"
          class="holo-chip shrink-0 min-w-11 min-h-11 px-3 flex items-center justify-center rounded border border-border text-text-dim hover:border-gold hover:text-gold">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
        </button>
        </div>
      </header>

      <main id="shell-content" class="flex-1 min-h-0 overflow-y-auto overscroll-contain"></main>

      <nav id="shell-tabbar" class="fixed inset-x-0 bottom-0 z-[45] flex justify-center items-end gap-0.5 sm:gap-2 px-1 pb-[calc(env(safe-area-inset-bottom)+0.5rem)] pointer-events-none" aria-label="${t('shell.sections')}">
        ${tabs.map((tab, i) => `
          <button type="button" data-tab-id="${tab.id}" style="--lean:${((i - (tabs.length - 1) / 2) * -2.6).toFixed(1)}deg"
            class="shell-tab-btn${tab.id === 'profile' ? ' max-sm:hidden' : ''} relative flex-1 min-w-0 max-w-[4.7rem] h-[3.6rem] pointer-events-auto flex flex-col items-center justify-center gap-0.5 text-text-dim">
            <span class="text-xl leading-none">${icon(tab.icon)}</span>
            <span class="w-full text-center text-[9px] sm:text-[10px] leading-none truncate px-0.5 uppercase tracking-wider">${tab.label}</span>
            <span data-tab-badge="${tab.id}" aria-hidden="true"
              class="hidden absolute -top-1.5 -right-1 min-w-4 h-4 px-1 rounded-full bg-crimson text-white text-[10px] leading-4 text-center"></span>
          </button>
        `).join('')}
      </nav>
    </div>
  `;

  const content = root.querySelector('#shell-content') as HTMLElement;
  // Which tab is showing, for CSS that restyles the chrome per screen (the map and colony
  // views drop the header band and keep only floating patches behind its contents). Stamped
  // on <html>, not the shell frame: the World Chat pill lives on <body>, outside the shell.
  const pageEl = document.documentElement;
  const logoutBtn = root.querySelector('#shell-logout') as HTMLButtonElement;
  const muteBtn = root.querySelector('#shell-mute') as HTMLButtonElement;
  const tabButtons = Array.from(root.querySelectorAll('.shell-tab-btn')) as HTMLButtonElement[];

  mountToastContainer(root);

  let tabTeardown: (() => void) | null = null;

  function paintActiveTab(activeId: string) {
    for (const btn of tabButtons) {
      const isActive = btn.dataset.tabId === activeId;
      btn.classList.toggle('text-gold', isActive);
      btn.classList.toggle('text-text-dim', !isActive);
    }
    const profileBtn = root.querySelector('#shell-profile');
    if (profileBtn) {
      profileBtn.classList.toggle('text-gold', activeId === 'profile');
      profileBtn.classList.toggle('border-gold', activeId === 'profile');
      profileBtn.classList.toggle('text-text-dim', activeId !== 'profile');
    }
  }

  function paintBadges(counts: Record<string, number>) {
    for (const btn of tabButtons) {
      const n = counts[btn.dataset.tabId!] ?? 0;
      const span = btn.querySelector('[data-tab-badge]') as HTMLSpanElement | null;
      if (!span) continue;
      span.textContent = n > 99 ? '99+' : String(n);
      span.classList.toggle('hidden', n === 0);
    }
  }

  /**
   * The mute control lives in the header, always reachable, per the brief.
   *
   * It reports what is actually happening rather than what was asked for:
   * browsers refuse to start audio before the first interaction, so the icon
   * stays "off" until a gesture has armed playback. Claiming "on" while
   * silence plays would be the worse lie.
   */
  function paintMute(state: { muted: boolean; playing: boolean }) {
    const sounding = !state.muted && state.playing;
    muteBtn.innerHTML = icon(sounding ? 'soundOn' : 'soundOff');
    muteBtn.setAttribute('aria-label', state.muted ? t('shell.unmuteSound') : t('shell.muteSound'));
    muteBtn.setAttribute('aria-pressed', String(state.muted));
    muteBtn.classList.toggle('text-gold', sounding);
    muteBtn.classList.toggle('text-text-dim', !sounding);
  }

  const audioPopup = root.querySelector('#shell-audio-popup') as HTMLElement;
  const muteToggleBtn = root.querySelector('#shell-mute-toggle') as HTMLButtonElement;
  const volumeSlider = root.querySelector('#shell-volume-slider') as HTMLInputElement;
  const sfxSlider = root.querySelector('#shell-sfx-slider') as HTMLInputElement;

  function paintAudioPopup(state: { muted: boolean; volume: number; sfxVolume: number }) {
    // "Mute" is one control over ALL sound, music and effects alike — see
    // effectiveSfxVolume in engine/audio.ts. The two sliders set the balance
    // between them, independent of whether anything is audible right now.
    muteToggleBtn.textContent = state.muted ? t('shell.unmute') : t('shell.mute');
    volumeSlider.value = String(Math.round(state.volume * 100));
    sfxSlider.value = String(Math.round(state.sfxVolume * 100));
  }

  let audioPopupOpen = false;
  function openAudioPopup() {
    audioPopupOpen = true;
    audioPopup.classList.remove('hidden');
    muteBtn.setAttribute('aria-expanded', 'true');
    paintAudioPopup(audioStore.get());
  }
  function closeAudioPopup() {
    audioPopupOpen = false;
    audioPopup.classList.add('hidden');
    muteBtn.setAttribute('aria-expanded', 'false');
  }
  function onMuteBtnClick(e: MouseEvent) {
    e.stopPropagation();
    if (audioPopupOpen) closeAudioPopup();
    else openAudioPopup();
  }
  function onDocClick(e: MouseEvent) {
    if (!audioPopupOpen) return;
    const wrap = root.querySelector('#shell-audio-wrap') as HTMLElement;
    if (!wrap.contains(e.target as Node)) closeAudioPopup();
  }
  function onDocKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape' && audioPopupOpen) closeAudioPopup();
  }

  muteToggleBtn.addEventListener('click', () => { toggleMuted(); paintAudioPopup(audioStore.get()); });
  volumeSlider.addEventListener('input', () => { setVolume(Number(volumeSlider.value) / 100); });
  sfxSlider.addEventListener('input', () => { setSfxVolume(Number(sfxSlider.value) / 100); });

  const unsubAudio = audioStore.subscribe(paintMute);
  const unsubBadges = tabBadgeStore.subscribe(paintBadges);

  function switchTab(id: string) {
    const tab = tabs.find((t) => t.id === id);
    if (!tab) return;
    tabTeardown?.();
    content.innerHTML = '';
    // Before mount, not after: CSS keyed on it changes the screen's box, and a canvas screen
    // sizes its buffer from that box while mounting.
    pageEl.dataset.activeTab = id;
    tabTeardown = tab.mount(content, { onLogout });
    activeTabStore.set(id);
    paintActiveTab(id);
    // Leaving the shop is the most likely moment for the balance to have moved.
    void refreshDeltaV();
    // Leaving the profile is when an equipped title may have changed.
    void refreshTitleBadge();
  }

  for (const btn of tabButtons) {
    btn.addEventListener('click', () => switchTab(btn.dataset.tabId!));
  }
  const profileBtnEl = root.querySelector('#shell-profile') as HTMLButtonElement | null;
  const onProfileClick = () => switchTab('profile');
  profileBtnEl?.addEventListener('click', onProfileClick);
  logoutBtn.addEventListener('click', onLogout);
  // The Delta-V balance is also the way into the shop.
  const deltaVBtn = root.querySelector('#shell-deltav') as HTMLButtonElement;
  const onDeltaVClick = () => switchTab('shop');
  // Other modules (the offer banner) ask for a tab by name.
  const onOpenTab = (e: Event) => { const id = (e as CustomEvent<string>).detail; if (typeof id === 'string') switchTab(id); };
  window.addEventListener('shell:open-tab', onOpenTab);
  deltaVBtn.addEventListener('click', onDeltaVClick);
  muteBtn.addEventListener('click', onMuteBtnClick);
  document.addEventListener('click', onDocClick);
  document.addEventListener('keydown', onDocKeydown);

  // The occasional call-to-action bubble over the Shop tab.
  const shopTabBtn = tabButtons.find((b) => b.dataset.tabId === 'shop');
  const shopTipTeardown = shopTabBtn ? mountShopTip({ button: shopTabBtn, isShopOpen: () => activeTabStore.get() === 'shop' }) : () => {};
  const clickTeardown = mountClickSound(root);
  const tiltTeardown = mountHoloTilt(root);
  const glitch = mountRandomGlitch();

  // ── Equipped title badge on the phone profile button ───────────────
  const titleSlots = Array.from(root.querySelectorAll<HTMLElement>('[data-shell-title]'));
  let titleDisposed = false;
  async function refreshTitleBadge(): Promise<void> {
    if (titleSlots.length === 0) return;
    try {
      const p = await fetchProfile();
      if (titleDisposed) return;
      for (const slot of titleSlots) {
        if (!p.titleLabel) {
          slot.hidden = true;
          slot.textContent = '';
          continue;
        }
        const a = titleBadgeAttrs(p.titleRarity);
        slot.className = 'title-plate pt-plate shell-title-badge';
        slot.setAttribute('data-rarity', a.rarity);
        slot.setAttribute('data-scene', a.scene);
        slot.style.setProperty('--title-tint', a.tint);
        slot.innerHTML = '<span></span>';
        (slot.firstElementChild as HTMLElement).textContent = p.titleLabel;
        slot.hidden = false;
      }
    } catch {
      // A failed read leaves the badges as they were; the header must never break on it.
    }
  }

  // ── Delta-V balance ────────────────────────────────────────────────
  const deltaVWrap = root.querySelector('#shell-deltav') as HTMLElement;
  const deltaVValue = root.querySelector('#shell-deltav-value') as HTMLElement;
  let balanceDisposed = false;

  async function refreshDeltaV(): Promise<void> {
    try {
      const { premium } = await fetchStoreBalance();
      if (balanceDisposed) return;
      deltaVValue.textContent = premium.toLocaleString('en-GB');
      deltaVWrap.hidden = false;
    } catch {
      // A failed balance read must never break the shell. The pill simply
      // stays as it was (or hidden, if this was the first attempt).
    }
  }

  void refreshDeltaV();
  // A purchase made elsewhere (a title bought from the profile) asks for an immediate re-read.
  const onDeltaVRefresh = () => void refreshDeltaV();
  window.addEventListener('deltav:refresh', onDeltaVRefresh);
  const onTitleChanged = () => void refreshTitleBadge();
  window.addEventListener('title:changed', onTitleChanged);
  // Spending happens on other screens (shop, instant-finish buttons), and
  // they have no handle on this header — so it re-reads on a timer and on
  // every tab switch rather than being pushed to.
  const balanceTimer = window.setInterval(() => void refreshDeltaV(), DELTA_V_REFRESH_MS);

  switchTab(tabs[0].id);

  return () => {
    titleDisposed = true;
    balanceDisposed = true;
    window.removeEventListener('deltav:refresh', onDeltaVRefresh);
    window.removeEventListener('title:changed', onTitleChanged);
    window.clearInterval(balanceTimer);
    shopTipTeardown();
    clickTeardown();
    tiltTeardown();
    glitch.teardown();
    delete pageEl.dataset.activeTab;
    tabTeardown?.();
    unmountToastContainer();
    unsubBadges();
    unsubAudio();
    muteBtn.removeEventListener('click', onMuteBtnClick);
    document.removeEventListener('click', onDocClick);
    document.removeEventListener('keydown', onDocKeydown);
    for (const btn of tabButtons) btn.replaceWith(btn.cloneNode(true));
    profileBtnEl?.removeEventListener('click', onProfileClick);
    logoutBtn.removeEventListener('click', onLogout);
    deltaVBtn.removeEventListener('click', onDeltaVClick);
    window.removeEventListener('shell:open-tab', onOpenTab);
  };
}
